import { useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import {
  A11y,
  Controller,
  EffectFade,
  Mousewheel,
  Navigation,
  Pagination,
} from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { getContent, isDDEnv, isWHEnv } from '@/utils/helper';

import Icon from '@/atoms/Icon/Icon';
import Pill from '@/atoms/Pill/Pill';
import CtfCta, { Cta } from '@/molecules/CTA/index';
import { DynamicAsset } from '@/molecules/Media/index';
import RichText from '@/molecules/RichText/RichText';

import { CtfCtaStyle } from '@/utils/constants';
import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';

const SWIPER_MODULES = [A11y, Controller, Mousewheel, Navigation, Pagination];

const SWIPER_NAVIGATION = {
  nextEl: '.aaco-carousel-hero__next',
  prevEl: '.aaco-carousel-hero__prev',
};

const SWIPER_PAGINATION = {
  clickable: true,
  el: '.aaco-carousel-hero__pagination',
  type: 'custom',
  renderCustom: function (_swiper, current, total) {
    const formattedCurrent = current < 10 ? `0${current}` : current;
    const formattedTotal = total < 10 ? `0${total}` : total;
    return `${formattedCurrent}/${formattedTotal}`;
  },
};

const SWIPER_SPEED = 1000;

// @TODO: Update left and right arrow carousel nav with proper icon < >
function CarouselHero({ assets, carouselContentStyle, entries, fields }) {
  const router = useRouter();

  const [firstSwiper, setFirstSwiper] = useState(null);
  const [secondSwiper, setSecondSwiper] = useState(null);

  const { backgroundImage, cards, title, id } = fields;

  return (
    <div
      className={`aaco-carousel-hero overflow-hidden ${
        !carouselContentStyle.compactSpace && 'pb-20'
      }`}
      aria-label={title}
      id={id}
    >
      <div
        className={!carouselContentStyle.compactSpace && 'container mx-auto'}
      >
        <div className="overflow-visible relative h-auto lg:min-h-[770px]">
          <div className="aaco-carousel-hero__bg absolute bg-warmer-grey md:dd-kr:bg-progressive-blue md:dd-en:bg-progressive-blue h-full w-screen transform right-0 translate-y-0">
            <div
              className="hidden dd-kr:block dd-en:block md:dd-kr:hidden md:dd-en:hidden bg-progressive-blue bg-cover bg-center w-full h-full rounded-[8px]"
              style={{
                backgroundImage: `url(${backgroundImage?.[0]?.original_secure_url})`,
              }}
            ></div>
          </div>

          <div className="aaco-carousel__swiper ">
            <Swiper
              controller={{ control: secondSwiper }}
              direction="horizontal"
              modules={SWIPER_MODULES}
              mousewheel={{
                forceToAxis: true,
              }}
              onSwiper={setFirstSwiper}
              spaceBetween={0}
              speed={SWIPER_SPEED}
            >
              {cards.map((card, index) => {
                const content = getContent(card.sys?.id, entries);
                const {
                  dynamicAsset,
                  cloudinaryAssets,
                  primaryCTA,
                  secondaryCTA,
                  tags = [],
                } = content.fields;
                const tag =
                  tags && tags.length > 0
                    ? getContent(tags[0].sys?.id, entries)
                    : null;
                return (
                  <SwiperSlide key={index}>
                    <div className="aaco-carousel-card__slide relative">
                      {tag && (
                        <Pill
                          style="pill-action absolute bottom-2.5 left-2.5 md:bottom-5 md:left-5 z-10 inline-flex items-center"
                          onClick={() =>
                            router.push(`/search?t=${tag?.fields?.slug}`)
                          }
                          small
                        >
                          {tag.fields.name}
                        </Pill>
                      )}
                      {(cloudinaryAssets || dynamicAsset) && (
                        <div className="aaco-card__image w-full max-h-[560px] md:h-full">
                          <DynamicAsset
                            assets={assets}
                            entries={entries}
                            container="container"
                            field={cloudinaryAssets ?? dynamicAsset}
                          />
                        </div>
                      )}
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
          <div className="aaco-carousel__content py-6 dd-kr:pt-[25px] dd-en:pt-[25px] pr-2 dd-kr:pr-[60px] dd-en:pr-[60px] md:pr-20 md:dd-kr:pr-[117px] md:dd-en:pr-[117px]">
            <Swiper
              controller={{ control: firstSwiper }}
              effect="fade"
              fadeEffect={{ crossFade: true }}
              modules={[...SWIPER_MODULES, EffectFade]}
              navigation={SWIPER_NAVIGATION}
              onSwiper={setSecondSwiper}
              pagination={SWIPER_PAGINATION}
              speed={SWIPER_SPEED}
            >
              {cards.map((card, index) => {
                const content = getContent(card.sys?.id, entries);
                let { primaryCTA, title, text } = content.fields;
                text =
                  text && text.sys
                    ? getContent(text.sys.id, entries)?.fields.text
                    : text;

                return (
                  <SwiperSlide key={index}>
                    <div className="content relative text-white">
                      <div className="flex-1 flex flex-col justify-center items-start mr-10">
                        <h2 className="text-h4 text-acacia dd-kr:text-h4-mobile dd-en:text-h4-mobile md:dd-kr:text-h5 md:dd-en:text-h5 font-normal font-heading mb-2.5 dd-kr:mb-0 dd-en:mb-0">
                          {title}
                        </h2>
                        {text && !text.content && (
                          <p className="text-acacia font-text text-body font-body opacity-50 dd-kr:opacity-100 dd-en:opacity-100 text-majesty-70 dd-kr:mt-[15px] dd-en:mt-[15px] dd-kr:text-80-white-on-dark-blue dd-en:text-80-white-on-dark-blue">
                            {text}
                          </p>
                        )}
                        {text &&
                          text.content &&
                          text.content.length > 0 &&
                          text.content.map(({ nodeType, content }, index) => (
                            <RichText
                              entries={entries}
                              nodeType={nodeType}
                              content={content}
                              key={index}
                              richTextStyle={{
                                paragraph: {
                                  customFont:
                                    TEXT_BASE_CLASS['body'] +
                                    (carouselContentStyle.hasBackground
                                      ? ' text-majesty-50'
                                      : ' dd-kr:text-80-white-on-dark-blue dd-en:text-80-white-on-dark-blue') +
                                    ' font-text',
                                  blockquoteFont: TEXT_BASE_CLASS['blockquote'],
                                },
                              }}
                            />
                          ))}
                        {primaryCTA && primaryCTA.sys && (
                          <div className="aaco-carousel-hero__cta mt-4 dd-kr:mt-5 dd-en:mt-5">
                            <CtfCta
                              field={primaryCTA}
                              entries={entries}
                              invert={true}
                            />
                          </div>
                        )}
                        {primaryCTA && !primaryCTA.sys && (
                          <div className="aaco-carousel-hero__cta mt-4 dd-kr:mt-5 dd-en:mt-5">
                            <Cta
                              buttonProps={{
                                invert: isWHEnv(),
                                name:
                                  router?.locale === 'ko-KR'
                                    ? '자세히 보기'
                                    : 'Learn more',
                                type: isDDEnv()
                                  ? CtfCtaStyle.DD_SECONDARY
                                  : CtfCtaStyle.SECONDARY,
                                style: 'w-fit text-acacia',
                              }}
                              iconProps={{
                                name: 'arrow-right',
                                height: 15,
                                width: 15,
                                style: 'text-acacia',
                                hasIconBorder: true,
                              }}
                              linkProps={{
                                url: primaryCTA,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
              <div className="swiper-control text-acacia flex items-center justify-end absolute bottom-0 right-5 dd-kr:right-0 dd-en:right-0 md:right-10 z-20">
                <div className="aaco-carousel-hero__prev">
                  <Icon name="chevron-left" height={12} width={6} />
                </div>
                <div className="mx-5 text-small flex-grow-0 w-auto">
                  <div className="aaco-carousel-hero__pagination"></div>
                </div>
                <div className="aaco-carousel-hero__next">
                  <Icon name="chevron-right" height={12} width={6} />
                </div>
              </div>
            </Swiper>
          </div>
        </div>
      </div>
    </div>
  );
}

CarouselHero.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
  carouselContentStyle: PropTypes.object,
};

CarouselHero.defaultProps = {
  assets: [],
  carouselContentStyle: {
    compactSpace: false,
    hasBackground: false,
    linkStyle: '',
  },
  entries: [],
  fields: null,
};

export default CarouselHero;
